import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { RCS_NOTIFY_EVENT_LABELS, RCS_NOTIFY_PREFS_DEFAULT, RcsNotifyEvents, RcsNotifyPrefs } from 'src/app/models/Rcs';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { RcsNotifyPrefsService } from 'src/app/shared/services/rcs-notify-prefs.service';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_EXTRA_EMAILS = 5;

@Component({
  selector: 'app-rcs-notify-prefs',
  templateUrl: './rcs-notify-prefs.component.html',
})
export class RcsNotifyPrefsComponent implements OnInit, OnDestroy {
  readonly notifyEventDefs = RCS_NOTIFY_EVENT_LABELS;
  notifyPrefs: RcsNotifyPrefs = RCS_NOTIFY_PREFS_DEFAULT;
  prefsLoading = true;
  prefsSaving = false;
  newEmail = '';
  emailError = '';
  /** "+ Adicionar e-mail": o campo só aparece depois do clique (mockup). */
  addEmailOpen = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private notifyPrefsService: RcsNotifyPrefsService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.prefsLoading = true;
    this.subscriptions.push(this.notifyPrefsService.get().subscribe(prefs => {
      this.notifyPrefs = prefs;
      this.prefsLoading = false;
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private errorMessage(response: any, fallback: string): string {
    return response?.error?.message || fallback;
  }

  // --- Preferências de aviso ("Como avisamos você") ------------------------------

  get canAddEmail(): boolean {
    return this.notifyPrefs.channels.email.addresses.length < MAX_EXTRA_EMAILS;
  }

  toggleAddEmail(): void {
    this.addEmailOpen = !this.addEmailOpen;
    this.emailError = '';
  }

  addEmail(): void {
    const email = this.newEmail.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.emailError = 'Informe um e-mail válido.';
      return;
    }
    if (this.notifyPrefs.channels.email.addresses.includes(email)) {
      this.emailError = 'Esse e-mail já está na lista.';
      return;
    }
    if (!this.canAddEmail) {
      this.emailError = `Só dá para adicionar até ${MAX_EXTRA_EMAILS} e-mails.`;
      return;
    }
    this.emailError = '';
    this.notifyPrefs = {
      ...this.notifyPrefs,
      channels: {
        ...this.notifyPrefs.channels,
        email: { ...this.notifyPrefs.channels.email, addresses: [...this.notifyPrefs.channels.email.addresses, email] },
      },
    };
    this.newEmail = '';
    this.addEmailOpen = false;
  }

  removeEmail(email: string): void {
    this.notifyPrefs = {
      ...this.notifyPrefs,
      channels: {
        ...this.notifyPrefs.channels,
        email: {
          ...this.notifyPrefs.channels.email,
          addresses: this.notifyPrefs.channels.email.addresses.filter(address => address !== email),
        },
      },
    };
  }

  toggleEmailChannel(): void {
    this.notifyPrefs = {
      ...this.notifyPrefs,
      channels: {
        ...this.notifyPrefs.channels,
        email: { ...this.notifyPrefs.channels.email, enabled: !this.notifyPrefs.channels.email.enabled },
      },
    };
  }

  isEventLocked(key: keyof RcsNotifyEvents): boolean {
    return this.notifyPrefs.locked_events.includes(key);
  }

  toggleEvent(key: keyof RcsNotifyEvents): void {
    if (this.isEventLocked(key)) {
      return;
    }
    this.notifyPrefs = {
      ...this.notifyPrefs,
      events: { ...this.notifyPrefs.events, [key]: !this.notifyPrefs.events[key] },
    };
  }

  savePrefs(): void {
    this.prefsSaving = true;
    this.subscriptions.push(
      this.notifyPrefsService.update(this.notifyPrefs).subscribe({
        next: prefs => {
          this.prefsSaving = false;
          this.notifyPrefs = prefs;
          this.toastService.success('Preferências de aviso salvas.');
        },
        error: response => {
          this.prefsSaving = false;
          this.toastService.error(this.errorMessage(response, 'Não foi possível salvar agora. Tente de novo.'));
        },
      })
    );
  }
}
